import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import api from '../api/api';
import { useTheme } from '../hooks/useTheme';
import { CreditCard, ArrowLeft, Gift, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { MEMBERSHIP_FEATURES, getFeaturesForTier } from '../utils/membershipFeatures';
import { Button } from '../components/ui/button';
import { Card, CardBandedHeader, CardBody } from '../components/ui/card';
import { Badge } from '../components/ui/badge';


export default function MembershipPlans() {
    const { isDarkMode } = useTheme();
    const { user, loading: authLoading } = useAuth();
        const [tiers, setTiers] = useState([]);
        const [loading, setLoading] = useState(true);
        const [subscribing, setSubscribing] = useState(null); // tier id being processed
        const [currentTierID, setCurrentTierID] = useState(null); // user's active tier
        const [billingCycle, setBillingCycle] = useState('monthly');
        const [referral, setReferral] = useState(null); // {code, canGenerate, usedCount, weeksEarned}
        const [referralCopied, setReferralCopied] = useState(false);
        const [referralLoading, setReferralLoading] = useState(false);
        // One box, two kinds of code. `promo` is a *preview* only — nothing is
        // redeemed until the user confirms a plan below.
        const [codeInput, setCodeInput] = useState('');
        const [applying, setApplying] = useState(false);
        const [codeMsg, setCodeMsg] = useState(null); // {type:'ok'|'bad', text}
        const [promo, setPromo] = useState(null); // {code, discountType, discountValue, tiers}
        const navigate = useNavigate();

    useEffect(() => {
        // Don't fetch until auth is settled and we have a confirmed user —
        // fetching while auth is still flapping post-login triggers a
        // remount/refetch storm against /membership/me.
        if (authLoading) return;
        if (!user) { setLoading(false); return; }
        const fetchData = async () => {
            try {
                const [tiersRes, membershipRes] = await Promise.allSettled([
                    api.get('/membership/tiers'),
                    api.get('/membership/me'),
                ]);

                if (tiersRes.status === 'fulfilled') {
                    // Students only see Student-audience tiers — Coach / Enterprise
                    // tiers must not leak into the regular user plans page.
                    const activeTiers = tiersRes.value.data.tiers.filter(
                        t => (t.IsActive === true || t.IsActive === 1) && (t.Audiences || []).includes('Student')
                    );
                    const sorted = activeTiers.sort((a, b) => a.Price - b.Price);
                    setTiers(sorted);
                } else {
                    setTiers([]);
                }

                if (membershipRes.status === 'fulfilled') {
                    const m = membershipRes.value.data;
                    if (m?.isActive && m?.TierID) setCurrentTierID(m.TierID);
                }
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [authLoading, user]);

    // Referral card state: the code itself, whether a new one may be issued yet,
    // and the lifetime tally of friends who've used this client's codes.
    const fetchReferral = React.useCallback(async () => {
        try {
            const res = await api.get('/membership/referral/status');
            setReferral(res.data || null);
        } catch (err) {
            console.error('Could not fetch referral status', err);
        }
    }, []);

    useEffect(() => {
        if (authLoading || !user) return;
        fetchReferral();
    }, [authLoading, user, fetchReferral]);

    const handleCopyReferral = () => {
        if (referral?.code) {
            navigator.clipboard.writeText(referral.code);
            setReferralCopied(true);
            setTimeout(() => setReferralCopied(false), 2000);
        }
    };

    // Same button for "get my first code" and "get a new one" — it's one action.
    // The server only issues a new code once the current one has been redeemed,
    // and hides the button entirely while we're still waiting on a friend.
    const handleGetReferralCode = async () => {
        setReferralLoading(true);
        try {
            await api.post('/membership/referral/generate');
            await fetchReferral();
        } catch {
            alert('Could not generate referral code.');
        } finally {
            setReferralLoading(false);
        }
    };

    const describeDiscount = (p) => {
        if (!p) return '';
        if (p.discountType === 'free') return 'free';
        if (p.discountType === 'percent') return `${Number(p.discountValue)}% off`;
        return `$${Number(p.discountValue || 0).toFixed(2)} off`;
    };

    // One box for both kinds of code. Promo codes are checked first; a code that
    // isn't one is tried as a friend's referral code. The two namespaces can't
    // collide — codes are generated unique across both — so this only decides
    // which lookup runs first, never which meaning wins.
    const handleApplyCode = async () => {
        const code = codeInput.trim().toUpperCase();
        if (!code) return;
        setApplying(true);
        setCodeMsg(null);
        try {
            const res = await api.post('/membership/code/preview', { code });
            setPromo(res.data);
            const n = res.data?.tiers?.length || 0;
            setCodeMsg({
                type: 'ok',
                text: `${code} applied — ${describeDiscount(res.data)} on ${n} plan${n === 1 ? '' : 's'} below.`,
            });
        } catch {
            try {
                const res = await api.post('/membership/referral/redeem', { referral_code: code });
                setCodeMsg({
                    type: 'ok',
                    text: res.data?.message || 'Referral applied! You and your friend each got +1 week.',
                });
                setCodeInput('');
                fetchReferral();
            } catch (err) {
                setCodeMsg({
                    type: 'bad',
                    text: err.response?.data?.error || "That code isn't valid or has expired.",
                });
            }
        } finally {
            setApplying(false);
        }
    };

    const clearPromo = () => {
        setPromo(null);
        setCodeInput('');
        setCodeMsg(null);
    };

    // The promo's effect on one tier, or null when this tier isn't covered.
    const promoFor = (tierId) => promo?.tiers?.find(t => t.TierID === tierId) || null;

    const handleSubscribe = async (tier) => {
        setSubscribing(tier.TierID);
        try {
            if (!user) {
                alert('You must be logged in to subscribe.');
                setSubscribing(null);
                return;
            }
            // Right after login the user object can carry only `clientid` and no
            // `email` yet — fall back to /user/me so a logged-in user isn't
            // wrongly told to log in.
            let email = user.email;
            if (!email) {
                try {
                    const me = await api.get('/user/me');
                    email = me.data?.email;
                } catch { /* handled by the guard below */ }
            }
            if (!email) {
                alert('We could not read your account email. Please refresh and try again.');
                setSubscribing(null);
                return;
            }

            // A $0 tier with no trial (Biz-Free) has no Stripe price behind it —
            // same rule Signup.js's stage-3 picker uses — so it bypasses Stripe
            // entirely instead of hitting create-checkout-session with an empty
            // price_id.
            // A 'free' (full grant) promo code covering this tier: the code alone
            // granted nothing — this confirmation is what actually claims it.
            if (promo && promo.discountType === 'free' && promoFor(tier.TierID)) {
                await api.post('/membership/code/redeem', { code: promo.code, tier_id: tier.TierID });
                window.location.href = '/membership';
                return;
            }

            const isFreeForever = Number(tier.Price) === 0 && !tier.TrialDays;
            if (isFreeForever) {
                // Downgrade to Free. The backend schedules it for the end of the
                // current plan's period — the member keeps their current features
                // until then (whatever tier they're on, paid or free) and can
                // switch back for free before then.
                const ok = window.confirm(
                    `Switch your plan to ${tier.TierName}?\n\nYou'll keep your current features until your plan's current period ends, and can switch back anytime before then at no charge.`
                );
                if (!ok) { setSubscribing(null); return; }
                await api.post('/membership/downgrade', { tier_id: tier.TierID });
                window.location.href = '/membership';
                return;
            }

            const response = await api.post('/membership/create-checkout-session', {
                price_id: tier.PaymentLink,
                tier_id: tier.TierID,
                customer_email: email
            });
            const { checkout_url } = response.data;
            if (checkout_url) {
                window.location.href = checkout_url;
            } else {
                alert('Could not start checkout.');
            }
        } catch (err) {
            alert(err.response?.data?.error || 'Error starting checkout.');
        } finally {
            setSubscribing(null);
        }
    };

    if (loading) {
        return (
            <div className={`flex items-center justify-center min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                <span className={`ml-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Loading membership plans...</span>
            </div>
        );
    }

    return (
        <div className={`max-w-5xl mx-auto py-10 px-4 space-y-8 ${isDarkMode ? 'bg-surface' : ''}`}>
            {/* Back — return to wherever the user came from (My Membership, the CSI
                result page, etc.); fall back to My Membership on a direct visit. */}
            <button
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-semibold transition-all ${isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}
                onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/membership'))}
            >
                <ArrowLeft size={18} /> Back
            </button>

            {/* Page heading */}
            <div>
                <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Choose a Membership Plan</h1>
                <p className={`mt-2 text-sm max-w-2xl ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    <span className="font-semibold">Lucky you!</span> We're currently offering free trial access for 60 days during our Beta, ending Oct. 1st. Please be sure to select either the <span className="font-semibold">Pro - Beta</span> or <span className="font-semibold">Pro - Beta (annual)</span> plan. You can cancel your membership and future payments anytime.
                </p>
            </div>

            {/* Billing Cycle Toggle */}
            <div className="flex justify-center">
                <div className={`inline-flex rounded-full p-1 text-sm font-semibold ${isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-100'}`}>
                    <button
                        onClick={() => setBillingCycle('monthly')}
                        className={`px-6 py-2 rounded-full transition-all ${billingCycle === 'monthly' ? 'bg-primary text-white shadow-sm' : isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        Monthly
                    </button>
                    <button
                        onClick={() => setBillingCycle('yearly')}
                        className={`px-6 py-2 rounded-full transition-all ${billingCycle === 'yearly' ? 'bg-primary text-white shadow-sm' : isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        Yearly
                    </button>
                </div>
            </div>

            {/* Refer a Friend + code entry — one card: share your own code above,
                enter someone else's below. Both kinds of code use the same box. */}
            <div className={`rounded-2xl border p-5 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <Gift className="text-primary" size={22} />
                        <div>
                            <p className={`font-bold text-sm ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Refer a Friend</p>
                            <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {referral?.code
                                    ? 'Share this code — you both get +1 week.'
                                    : 'Both you and your friend get +1 week added to your membership!'}
                            </p>
                        </div>
                    </div>
                    {/* The code stays on the card once issued. The button is the same
                        action either way ("get my first" / "get a new one"), and is
                        simply absent while we're waiting on a friend to use the
                        current code — no button is explanation enough. */}
                    {referral?.code ? (
                        <div className="flex items-center gap-2">
                            <span className={`font-mono text-base font-bold tracking-widest px-4 py-2 rounded-xl border ${isDarkMode ? 'bg-slate-900 border-slate-600 text-primary' : 'bg-slate-50 border-slate-200 text-primary-dark'}`}>
                                {referral.code}
                            </span>
                            <Button variant="secondary" size="sm" onClick={handleCopyReferral}>
                                {referralCopied ? <Check size={16} className="text-success" /> : <Copy size={16} />}
                                {referralCopied ? 'Copied' : 'Copy'}
                            </Button>
                        </div>
                    ) : referral?.canGenerate !== false ? (
                        <Button variant="primary" size="sm" loading={referralLoading} onClick={handleGetReferralCode}>
                            <Gift size={16} /> Get my code
                        </Button>
                    ) : null}
                </div>

                {/* Lifetime tally — independent of whether a code is currently out,
                    so it stays put right after rotating to a fresh one. */}
                {referral?.usedCount > 0 && (
                    <p className="mt-3 flex items-start gap-2 text-sm font-medium text-success">
                        <Check size={16} className="shrink-0 mt-0.5" />
                        <span>
                            <strong>{referral.usedCount} friend{referral.usedCount === 1 ? '' : 's'} used your code</strong>
                            {' '}— +{referral.weeksEarned} week{referral.weeksEarned === 1 ? '' : 's'} added to your membership.
                        </span>
                    </p>
                )}

                <div className="border-t border-border my-4" />

                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <p className={`font-bold text-sm ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Have a code?</p>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Enter a friend&rsquo;s referral code or a promo code.</p>
                    </div>
                    <div className="flex gap-2">
                        <input
                            value={codeInput}
                            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleApplyCode(); }}
                            placeholder="ENTER CODE"
                            disabled={!!promo}
                            className={`w-44 px-3 py-2 rounded-xl border font-mono font-semibold tracking-widest uppercase disabled:opacity-60 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-100' : 'border-slate-300'}`}
                        />
                        {promo ? (
                            <Button variant="secondary" size="sm" onClick={clearPromo}>Remove</Button>
                        ) : (
                            <Button variant="primary" size="sm" loading={applying} disabled={!codeInput.trim()} onClick={handleApplyCode}>
                                {applying ? 'Applying\u2026' : 'Apply'}
                            </Button>
                        )}
                    </div>
                </div>

                {codeMsg && (
                    <p className={`mt-3 text-sm font-medium ${codeMsg.type === 'ok' ? 'text-success' : 'text-error'}`}>{codeMsg.text}</p>
                )}
            </div>

            {/* Plans in columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                {tiers.map((tier, idx) => {
                    // "Most Popular" is now an admin-set flag per tier (not a
                    // hardcoded middle-card rule).
                    const isPro = tier.IsMostPopular === true || tier.IsMostPopular === 1;
                    const isCurrentPlan = currentTierID === tier.TierID;
                    const currentIdx = tiers.findIndex(t => t.TierID === currentTierID);
                    const isUpgrade = currentTierID && !isCurrentPlan && idx > currentIdx;
                    const isDowngrade = currentTierID && !isCurrentPlan && idx < currentIdx;
                    return (
                        <Card
                            key={tier.TierID}
                            variant={isPro ? 'elevated' : 'standard'}
                            className={`flex flex-col relative ${promoFor(tier.TierID) ? 'ring-2 ring-primary/40' : isCurrentPlan ? 'ring-2 ring-primary/25' : ''}`}
                        >
                            {/* Top banner */}
                            <CardBandedHeader className="relative">
                                {/* Badges — top right */}
                                <div className="absolute top-4 right-4 flex flex-col items-end gap-1">
                                    {isCurrentPlan && <Badge variant="primary">✓ Current Plan</Badge>}
                                    {isPro && !isCurrentPlan && <Badge variant="primary">Most Popular</Badge>}
                                </div>
                                <p className="text-white/45 text-xs font-bold uppercase tracking-widest mb-1">Plan</p>
                                <h2 className="text-2xl font-bold text-white pr-24">{tier.TierName}</h2>
                            </CardBandedHeader>

                            {/* Body */}
                            <CardBody className="flex-1 flex flex-col gap-5">
                                {/* Price */}
                                <div className="text-center">
                                    {promoFor(tier.TierID) ? (
                                        <>
                                            <div className="text-sm font-semibold text-ink-soft line-through">${Number(tier.Price).toFixed(2)}</div>
                                            <div className="flex items-baseline justify-center gap-1">
                                                <span className="text-4xl font-extrabold text-primary">${Number(promoFor(tier.TierID).DiscountedPrice).toFixed(2)}</span>
                                                <span className="text-sm font-semibold text-ink-soft">/mo</span>
                                            </div>
                                            <span className="inline-block mt-2 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-success/10 text-success">
                                                {promo.code} &middot; {describeDiscount(promo)}
                                            </span>
                                        </>
                                    ) : (
                                    <div className="flex items-baseline justify-center gap-1">
                                        <span className="text-4xl font-extrabold text-primary">${Number(tier.Price).toFixed(2)}</span>
                                        <span className="text-sm font-semibold text-ink-soft">/mo</span>
                                    </div>
                                    )}
                                    {tier.TierDescription && (
                                        <p className="mt-2 text-sm text-ink-soft">{tier.TierDescription}</p>
                                    )}
                                </div>

                                {/* Divider */}
                                <div className="border-t border-border" />

                                {/* Features */}
                                <ul className="space-y-2 flex-1">
                                    {MEMBERSHIP_FEATURES.map((feature, i) => {
                                        const { included, badge } = getFeaturesForTier(idx)[i];
                                        return (
                                            <li key={`feature-${idx}-${i}`} className={`flex items-center gap-2 text-sm ${included ? 'text-ink' : 'text-ink-soft'}`}>
                                                <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${included ? 'bg-primary-tint text-primary' : 'bg-section text-ink-soft'}`}>
                                                    {included ? '✓' : '✗'}
                                                </span>
                                                <span className={included ? '' : 'line-through'}>{feature}</span>
                                                {included && badge && (
                                                    <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-primary-tint text-primary">
                                                        {badge}
                                                    </span>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>

                                {/* CTA Button */}
                                <Button
                                    variant={isCurrentPlan ? 'secondary' : isDowngrade ? 'secondary' : 'primary'}
                                    className={`w-full mt-2 ${isCurrentPlan ? 'bg-primary-tint text-primary border border-primary/30 cursor-default' : ''}`}
                                    onClick={() => !isCurrentPlan && handleSubscribe(tier, idx)}
                                    disabled={isCurrentPlan}
                                    loading={subscribing === tier.TierID}
                                >
                                    <CreditCard size={18} />
                                    {isCurrentPlan
                                        ? 'Current Plan'
                                        : subscribing === tier.TierID
                                        ? 'Processing...'
                                        : isUpgrade
                                        ? 'Upgrade'
                                        : isDowngrade
                                        ? 'Downgrade'
                                        : 'Subscribe'}
                                </Button>
                            </CardBody>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
}
